import React, { useState } from 'react';
import {
  Modal,
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ImageAnnotationTool from './ImageAnnotationTool';
import MiddleController from './MiddleController';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line
import { ADD_CAMERA, SET_CAMERA_IMAGE, CAMERA_ADDED } from 'store/actions';
import { createCamera } from '../../../../../api/FM/videoPageApis';

const AddCameraButton = () => {
  const [openAddCamera, setOpenAddCamera] = useState(false);
  const [openAnnotate, setOpenAnnotate] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [deviceName, setDeviceName] = useState('');
  const [cameraAddress, setCameraAddress] = useState('');
  const [framesSkipped, setFramesSkipped] = useState('');
  const [isActive, setIsActive] = useState(true);
  // eslint-disable-next-line
  const [errors, setErrors] = useState({});
  const [cameraImage, setCameraImage] = useState(null);
  const [cameraId, setCameraId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  const [pipeline, setPipeline] = useState('');

  const dispatch = useDispatch();

  const handleOpenAddCamera = () => setOpenAddCamera(true);
  const handleCloseAddCamera = () => setOpenAddCamera(false);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenAnnotate = async () => {
    const abortController = new AbortController();

    try {
      const response = await createCamera(
        {
          device_name: deviceName,
          camera_address: cameraAddress,
          process_skip_frame: parseInt(framesSkipped) || 0,
          active: isActive
        },
        abortController.signal
      );

      if (response?.status === 403) {
        setSnackbar({
          open: true,
          message: response.data,
          severity: 'error'
        });
        return;
      }

      setCameraImage(response.base64Image);
      setCameraId(response.camera_id);
      dispatch({ type: SET_CAMERA_IMAGE, payload: response.base64Image });
      setOpenAnnotate(true);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was aborted');
        return;
      }
      console.error('Error creating camera:', error);
      setCameraImage('');
      setCameraId('');
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create camera',
        severity: 'error'
      });
    }

    return () => {
      abortController.abort();
    };
  };

  const handleCloseAnnotate = () => {
    setIsSubmitButtonDisabled(false);
    setOpenAnnotate(false);
  };
  // eslint-disable-next-line
  const validate = () => {
    const newErrors = {};
    if (!deviceName) newErrors.deviceName = 'Device Name is required';
    if (!cameraAddress) newErrors.cameraAddress = 'Camera Address is required';
    if (!framesSkipped) newErrors.framesSkipped = 'Please select the number of frames to skip';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        device_name: deviceName,
        camera_address: cameraAddress,
        process_skip_frame: parseInt(framesSkipped),
        active: isActive
      };
      console.log('Creating camera with data:', data);
      const response = await createCamera(data);
      console.log('Camera created successfully:', response);
      dispatch({ type: CAMERA_ADDED });
      console.log('Dispatched CAMERA_ADDED action');
      handleCloseAddCamera();
      setSnackbar({
        open: true,
        message: 'Camera added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating camera:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add camera',
        severity: 'error'
      });
    }
  };

  // const connectCamera = async () => {
  //   try {
  //     const isValid = validate();
  //     if (Object.keys(isValid).length === 0) {
  //       dispatch({ type: ADD_CAMERA, payload: { mode: 'Add Camera', addCamera: false } });
  //       dispatch({ type: CAMERA_ADDED });
  //     }
  //   } catch (error) {
  //     console.error('Error connecting camera:', error);
  //   }
  // };

  const pipelineArray = [
    'src/configs/machine_learning/computer_vision/worker_safety_tracker.json',
    'src/configs/machine_learning/computer_vision/truck_metadata_tracker.json',
    'src/configs/machine_learning/computer_vision/Dock_tracker.json',
    'src/configs/machine_learning/computer_vision/MHE_tracker.json'
  ];

  return (
    <>
      <div className="relative flex flex-col items-center group">
        <button
          onClick={handleOpenAddCamera}
          className="flex items-center justify-center bg-[#2196f3] hover:bg-[#0e63a8] active:bg-[#0e63a8] text-white font-semibold rounded-full w-8 h-8 transition-all duration-300 ease-in-out shadow-md hover:shadow-lg relative"
        >
          <AddIcon className="text-2xl" />
        </button>

        {/* Tooltip - Keeps Showing When Hovering Over It */}
        <span className="absolute left-1/2 -translate-x-1/2 z-50 top-full mt-2 bg-blue-100 text-blue-900 text-sm px-3 py-1 rounded-lg border border-blue-300 shadow-md scale-95 opacity-0 pointer-events-none group-hover:scale-100 group-hover:opacity-100 transition-all duration-300 ease-in-out delay-100">
          Add Camera
        </span>
      </div>

      <Modal open={openAddCamera} onClose={handleCloseAddCamera}>
        <Box
          component="form"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px'
          }}
          onSubmit={handleSubmit}
        >
          <TextField
            label="Device Name"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            fullWidth
            margin="normal"
            error={!!errors.deviceName}
            helperText={errors.deviceName}
          />
          <TextField
            label="Camera Address"
            value={cameraAddress}
            onChange={(e) => setCameraAddress(e.target.value)}
            fullWidth
            margin="normal"
            error={!!errors.cameraAddress}
            helperText={errors.cameraAddress}
          />
          <FormControl fullWidth margin="normal" error={!!errors.framesSkipped}>
            <InputLabel>Frames Skipped</InputLabel>
            <Select value={framesSkipped} onChange={(e) => setFramesSkipped(e.target.value)} label="Frames Skipped">
              {[...Array(10)].map((_, i) => (
                <MenuItem key={i} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.framesSkipped}</FormHelperText>
          </FormControl>

          <FormControl fullWidth margin="normal" error={!!errors.pipeline}>
            <InputLabel>Select Pipeline</InputLabel>
            <Select value={pipeline} onChange={(e) => setPipeline(e.target.value)} label="Select Pipeline">
              {pipelineArray.map((path, index) => (
                <MenuItem key={index} value={path}>
                  {path}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.pipeline}</FormHelperText>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />}
            label="Camera Active"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <button
              onClick={handleOpenAnnotate}
              type="button"
              className="border-none cursor-pointer text-center px-6 py-2.5 text-white font-semibold bg-[#2e7d32] rounded-[50px] flex items-center justify-center gap-2 hover:bg-[#1b5e20] active:bg-[#388e3c] active:transform active:scale-95 transition-all shadow-md hover:shadow-lg"
            >
              Annotate
            </button>
            <button
              onClick={() => {
                console.log('Submitting camera and triggering refetch');
                dispatch({ type: CAMERA_ADDED });
                handleCloseAddCamera();
              }}
              type="submit"
              disabled={isSubmitButtonDisabled}
              className="border-none cursor-pointer text-center px-6 py-2.5 text-white font-semibold bg-[#2e7d32] rounded-[50px] flex items-center justify-center gap-2 hover:bg-[#1b5e20] active:bg-[#388e3c] active:transform active:scale-95 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit
            </button>
          </Box>
        </Box>
      </Modal>
      <Modal open={openAnnotate} onClose={handleCloseAnnotate} fullScreen>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            p: 4,
            overflow: 'auto'
          }}
          className="bg-slate-200"
        >
          <ImageAnnotationTool cameraImage={cameraImage} cameraId={cameraId} handleCloseAnnotate={handleCloseAnnotate} />
          <div className="w-full bg-slate-200">
            <MiddleController handleCloseAnnotate={handleCloseAnnotate} cameraId={cameraId} />
          </div>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddCameraButton;
